import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Clapperboard, Users } from "lucide-react";

const NAMES = { journey: "A day in the life of a PM", features: "Feature tour", highlights: "2-minute highlights", pmo: "A day in the life of a PMO leader" };

export const DemoAnalyticsPanel = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/demo-analytics").then(({ data }) => setData(data)).catch(() => setData({}));
  }, []);

  if (!data || (Object.keys(data.videos || {}).length === 0 && Object.keys(data.role_picks || {}).length === 0)) return null;
  const videos = data.videos || {};
  const picks = Object.entries(data.role_picks || {}).sort((a, b) => b[1] - a[1]);
  const pickMax = Math.max(1, ...picks.map(([, n]) => n));
  return (
    <div className="border border-border rounded-xl p-5 mb-8" data-testid="demo-analytics-panel">
      <h3 className="font-display font-bold flex items-center gap-2 mb-4"><Clapperboard className="h-4 w-4 text-primary" /> Demo analytics <span className="text-xs text-muted-foreground font-normal">where viewers drop off</span></h3>
      {picks.length > 0 && (
        <div className="rounded-lg border border-border p-4 mb-5" data-testid="demo-role-picks">
          <p className="font-semibold text-sm flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-primary" /> Landing page role picks <span className="text-xs text-muted-foreground font-normal">who your landing page attracts</span></p>
          <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
            {picks.map(([video, n]) => (
              <div key={video} className="flex items-center gap-2 text-[11px]" data-testid={`role-pick-${video}`}>
                <span className="w-40 truncate text-muted-foreground shrink-0">{NAMES[video] || video}</span>
                <div className="flex-1 h-2.5 rounded bg-secondary overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${(n / pickMax) * 100}%` }} />
                </div>
                <span className="w-6 tabular-nums text-muted-foreground shrink-0">{n}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {Object.entries(videos).map(([video, v]) => {
          const max = Math.max(1, ...v.chapters.map((c) => c.viewers));
          return (
            <div key={video} className="rounded-lg border border-border p-4" data-testid={`demo-analytics-${video}`}>
              <p className="font-semibold text-sm">{NAMES[video] || video}</p>
              <p className="text-xs text-muted-foreground mb-3">
                {v.starts} starts · {v.completes} finished ({v.starts ? Math.round((v.completes / v.starts) * 100) : 0}%) · {v.ctas} CTA clicks
                {v.avg_rating ? <span className="text-amber-600 font-semibold"> · ★ {v.avg_rating} ({v.rating_count})</span> : null}
              </p>
              <div className="space-y-1">
                {v.chapters.map((c) => (
                  <div key={c.chapter} className="flex items-center gap-2 text-[11px]" title={c.title}>
                    <span className="w-5 text-right tabular-nums text-muted-foreground shrink-0">{c.chapter}</span>
                    <div className="flex-1 h-2.5 rounded bg-secondary overflow-hidden">
                      <div className="h-full bg-[#0044FF]" style={{ width: `${(c.viewers / max) * 100}%` }} />
                    </div>
                    <span className="w-6 tabular-nums text-muted-foreground shrink-0">{c.viewers}</span>
                  </div>
                ))}
              </div>
              {v.comments?.length > 0 && (
                <div className="mt-3 space-y-1.5" data-testid={`demo-comments-${video}`}>
                  {v.comments.map((c, i) => (
                    <p key={i} className="text-[11px] text-muted-foreground border-l-2 border-[#0044FF] pl-2">
                      {c.rating ? <span className="text-amber-600">{"★".repeat(c.rating)}</span> : null} “{c.comment}”
                    </p>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
